import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api_url } from "../../config/config";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../Context/Createcontext";
import addToCartIcon from "../../Icons/addToCartIcon.svg";
import { toast } from "react-hot-toast";
import Popup from "../common/Popup";
import wish from "../../images/wish.svg";
import wishlist from "../../images/wishlist_nofill.svg";
import { useWishlist } from "../../Context/WishlistContext";

/* ─────────────────────────────────────────────────
   PRICE HELPER
   variant.mrp              → original price (string|null)
   variant.discounted_price → sale price     (number|null)
   Rules:
   • discounted_price valid (>0 AND < mrp) → show strikethrough mrp + disc + badge
   • otherwise → show only mrp, no strikethrough, no badge
───────────────────────────────────────────────── */
const getPriceInfo = (variants, discountPercent) => {
  if (!variants || variants.length === 0)
    return { mrp: null, finalPrice: null, showDiscount: false, pct: 0 };
  const sorted = [...variants]
    .map((v) => ({
      ...v,
      _mrp: parseFloat(v.mrp) || 0,
      _disc: parseFloat(v.discounted_price) || 0,
    }))
    .filter((v) => v._mrp > 0)
    .sort((a, b) => a._mrp - b._mrp);
  if (!sorted.length)
    return { mrp: null, finalPrice: null, showDiscount: false, pct: 0 };
  const best = sorted[0];
  const showDiscount = best._disc > 0 && best._disc < best._mrp;
  return {
    mrp: best._mrp,
    finalPrice: showDiscount ? best._disc : best._mrp,
    showDiscount,
    pct: showDiscount
      ? discountPercent ||
        Math.round(((best._mrp - best._disc) / best._mrp) * 100)
      : 0,
  };
};

const FeaturedProduct_home = () => {
  const {
    handleAddToCart,
    showLoginPopup,
    setShowLoginPopup,
    handleRemoveFromCart,
    cartItems,
    setCartItems,
  } = useCart();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [tooltipOpen, setTooltipOpen] = useState(null);
  const {
    wishlist: wishlistItems,
    addToWishlist,
    removeFromWishlist,
    loading: wishlistLoading,
  } = useWishlist();

  const scrollRef = useRef(null);
  const navigate = useRef(useNavigate()).current;

  useEffect(() => {
    const fetchFeaturedProduct = async () => {
      try {
        const response = await fetch(
          `${api_url}/featured/product?page=1&limit=100`,
          {
            headers: { "Content-Type": "application/json", "x-auth": "false" },
          },
        );
        if (!response.ok) throw new Error("Failed to fetch featured products");
        const data = await response.json();
        setFeaturedProducts(data.data || []);
      } catch (error) {
        console.error("Error fetching featured products:", error);
      }
    };
    fetchFeaturedProduct();
  }, []);

  const displayedProducts = featuredProducts.slice(0, 10);

  const OutOfQuantityCheck = (variants) =>
    variants.every((v) => !v.quantity || v.quantity === 0);
  const getCartQuantity = (variantId) => {
    const item = cartItems.find((ci) => ci.variant_id === variantId);
    return item ? item.quantity : 0;
  };
  const handleProductClick = (product_id, item) =>
    navigate(`/products/${item.name?.toLowerCase().replace(/\s+/g, "-")}`, {
      state: { product_id },
    });

  const handleIncrease = async (event, variant, item) => {
    event.preventDefault();
    if (!variant?.variant_id) return;
    const qty = getCartQuantity(variant.variant_id);
    const res = await fetch(`${api_url}/cart`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        product_id: variant.product_id,
        quantity: qty + 1,
        variant_id: variant.variant_id,
      }),
    });
    const data = await res.json();
    if (
      !res.ok ||
      data.message?.toLowerCase().includes("stock not available")
    ) {
      toast.error(`${item.name} is out of stock`);
      return;
    }
    setCartItems((prev) =>
      prev.map((ci) =>
        ci.variant_id === variant.variant_id
          ? { ...ci, quantity: ci.quantity + 1 }
          : ci,
      ),
    );
  };

  const handleDecrease = async (event, variant) => {
    event.preventDefault();
    if (!variant?.variant_id) return;
    const qty = getCartQuantity(variant.variant_id);
    if (qty === 1) {
      await handleRemoveFromCart(variant.product_id, variant.variant_id);
      setCartItems((prev) =>
        prev.filter((ci) => ci.variant_id !== variant.variant_id),
      );
      return;
    }
    const res = await fetch(`${api_url}/cart`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        product_id: variant.product_id,
        quantity: qty - 1,
        variant_id: variant.variant_id,
      }),
    });
    const data = await res.json();
    if (
      !res.ok ||
      data.message?.toLowerCase().includes("stock not available")
    ) {
      toast.error("Something went wrong");
      return;
    }
    setCartItems((prev) =>
      prev.map((ci) =>
        ci.variant_id === variant.variant_id
          ? { ...ci, quantity: ci.quantity - 1 }
          : ci,
      ),
    );
  };

  if (!featuredProducts || featuredProducts.length === 0) return null;

  return (
    <>
      {displayedProducts.length > 0 && (
        <section className="relative w-full py-8 sm:py-6 lg:py-6 font-poppins overflow-hidden bg-white">
          <div className="relative px-4 sm:px-6 lg:px-6 xl:px-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-7 sm:mb-9">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-[2px] bg-[#ff601e] rounded-full" />
                  <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-widest text-[#ff601e]">
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#ff601e"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    Featured
                  </span>
                </div>
                <h2 className="font-bold text-[22px] sm:text-[26px] lg:text-[30px] text-[#1e4a46] leading-tight">
                  Featured Medicines
                </h2>
                <p className="text-[13px] text-[#8faba9] mt-1 hidden sm:block">
                  Handpicked medicines &amp; health essentials just for you
                </p>
              </div>
              <Link to="/featured-products">
                <button className="flex items-center gap-1.5 bg-[#162c2a] hover:bg-[#ff601e] text-white font-semibold text-[11px] sm:text-[12px] px-4 h-8 rounded-xl transition-all duration-200 shadow-[0_2px_8px_rgba(22,44,42,0.25)] hover:shadow-[0_4px_14px_rgba(255,96,30,0.4)] hover:-translate-y-0.5">
                  View All
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              </Link>
            </div>

            {/* Carousel */}
            <div className="relative">
              <button
                onClick={() =>
                  scrollRef.current?.scrollBy({
                    left: -220,
                    behavior: "smooth",
                  })
                }
                className="absolute -left-3 sm:-left-5 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white border-[1.5px] border-[#e2eaea] shadow-[0_2px_10px_rgba(22,44,42,0.1)] flex items-center justify-center text-[#374747] hover:bg-[#eaf4f3] hover:border-[#5fbdb3] hover:text-[#1e4a46] transition-all duration-200"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 12H5M12 5l-7 7 7 7" />
                </svg>
              </button>

              <div
                ref={scrollRef}
                className="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-hide scroll-smooth py-2 px-1"
              >
                {displayedProducts.map((item, index) => {
                  const isWishlisted = wishlistItems.some(
                    (w) =>
                      w.product_id === item.product_id &&
                      w.variant_id === item.variant?.[0]?.variant_id,
                  );
                  const isOutOfStock = OutOfQuantityCheck(item.variant || []);
                  const cartQty = getCartQuantity(
                    item.variant?.[0]?.variant_id,
                  );
                  const isMultiVariant = (item?.variant?.length || 0) > 1;

                  /* ── price ── */
                  const { mrp, finalPrice, showDiscount, pct } = getPriceInfo(
                    item.variant || [],
                    item.discount_percent,
                  );

                  const isOrange = index % 2 === 1;
                  const accentGrad = isOrange
                    ? "from-[#ff601e] to-[#e55519]"
                    : "from-[#1e4a46] via-[#5fbdb3] to-[#1e4a46]";
                  const borderHov = isOrange
                    ? "hover:border-[#ff601e]/50"
                    : "hover:border-[#5fbdb3]";
                  const imgBg = isOrange ? "bg-[#fff8f5]" : "bg-[#f4fbfa]";
                  const discCls = isOrange
                    ? "text-[#ff601e]"
                    : "text-[#5fbdb3]";
                  const btnCls = isOrange
                    ? "bg-[#ff601e] hover:bg-[#e55519] shadow-[0_2px_8px_rgba(255,96,30,0.3)] hover:shadow-[0_4px_14px_rgba(255,96,30,0.5)]"
                    : "bg-[#1e4a46] hover:bg-[#ff601e] shadow-[0_2px_8px_rgba(22,44,42,0.2)] hover:shadow-[0_4px_14px_rgba(255,96,30,0.4)]";

                  return (
                    <div
                      key={index}
                      className={`group flex-shrink-0 w-[235px] 
                        bg-white border-[1.5px] border-[#e2eaea] ${borderHov} rounded-2xl overflow-hidden
                        shadow-[0_2px_14px_rgba(22,44,42,0.07)] hover:shadow-[0_10px_36px_rgba(22,44,42,0.16)]
                        hover:-translate-y-2 transition-all duration-300 flex flex-col relative`}
                    >
                      <div
                        className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${accentGrad} opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10`}
                      />

                      {/* Discount badge — only when real discount */}
                      {showDiscount && pct > 0 && (
                        <div className="absolute top-3 right-3 bg-[#ff601e] text-white text-[9px] sm:text-[10px] font-bold w-9 h-9 sm:w-10 sm:h-10 rounded-full flex flex-col items-center justify-center shadow-[0_2px_8px_rgba(255,96,30,0.45)] z-10 leading-tight text-center">
                          {pct}%<br />
                          OFF
                        </div>
                      )}

                      {/* Wishlist */}
                      <button
                        className="absolute top-3 left-3 z-10 w-7 h-7 sm:w-8 sm:h-8 bg-white rounded-xl border border-[#e2eaea] flex items-center justify-center shadow-sm hover:border-[#5fbdb3] hover:bg-[#eaf4f3] transition-all duration-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!localStorage.getItem("authToken")) {
                            setShowLoginPopup(true);
                            return;
                          }
                          isWishlisted
                            ? removeFromWishlist(
                                item.product_id,
                                item.variant?.[0]?.variant_id,
                              )
                            : addToWishlist(item, item.variant?.[0]);
                        }}
                        disabled={wishlistLoading}
                        onMouseEnter={() => setTooltipOpen(index)}
                        onMouseLeave={() => setTooltipOpen(null)}
                      >
                        <img
                          src={isWishlisted ? wish : wishlist}
                          alt="wishlist"
                          className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                        />
                        {tooltipOpen === index && (
                          <div className="absolute left-9 top-0 bg-[#162c2a] text-white text-[10px] font-medium px-2 py-1 rounded-lg whitespace-nowrap shadow z-30">
                            {isWishlisted
                              ? "Remove from wishlist"
                              : "Add to wishlist"}
                          </div>
                        )}
                      </button>

                      {/* Image */}
                      <div
                        className={`w-full h-full flex items-center justify-center ${imgBg} cursor-pointer overflow-hidden`}
                        onClick={() =>
                          handleProductClick(item.product_id, item)
                        }
                      >
                        <img
                          src={
                            item?.product_images?.image_url?.primary_image ||
                            "/default.svg"
                          }
                          alt={item.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/default.svg";
                          }}
                        />
                      </div>

                      <div className="w-full h-px bg-[#f1f5f5]" />

                      {/* Info */}
                      <div
                        className="px-3 pt-3 pb-2 flex-1 flex flex-col gap-1.5 cursor-pointer"
                        onClick={() =>
                          handleProductClick(item.product_id, item)
                        }
                      >
                        <h3 className="font-semibold text-[12px] sm:text-[13px] text-[#1e4a46] line-clamp-2 leading-snug group-hover:text-[#ff601e] transition-colors duration-200">
                          {item.name}
                        </h3>

                        {/* Price */}
                        <div className="flex items-baseline gap-2 flex-wrap">
                          {mrp ? (
                            <>
                              {showDiscount && (
                                <span className="text-[11px] text-[#8faba9] line-through">
                                  ₹{mrp}
                                </span>
                              )}
                              <span className="font-bold text-[14px] sm:text-[15px] text-[#1e4a46]">
                                ₹{finalPrice}
                              </span>
                              {showDiscount && pct > 0 && (
                                <span
                                  className={`text-[10px] font-semibold ${discCls}`}
                                >
                                  {pct}% off
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-[12px] text-[#8faba9] font-medium">
                              0.00
                            </span>
                          )}
                        </div>

                        {isOutOfStock && (
                          <span className="self-start text-[10px] font-semibold text-red-500 bg-red-50 border border-red-100 rounded-lg px-2 py-0.5">
                            Out of Stock
                          </span>
                        )}
                      </div>

                      {/* Cart */}
                      <div className="px-3 pb-3 pt-1">
                        {isMultiVariant ? (
                          <button
                            className={`w-full h-9 ${btnCls} disabled:bg-[#c5d5d4] disabled:cursor-not-allowed text-white text-[11px] sm:text-[12px] font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-all duration-200`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!localStorage.getItem("authToken")) {
                                setShowLoginPopup(true);
                                return;
                              }
                              handleProductClick(item.product_id, item);
                            }}
                          >
                            <img
                              src={addToCartIcon}
                              alt="Cart"
                              className="w-3.5 h-3.5 object-contain"
                            />
                            Choose Variant
                          </button>
                        ) : cartQty > 0 ? (
                          <div className="flex items-center justify-between bg-[#eaf4f3] border border-[#d4ecea] rounded-xl h-9 px-2">
                            <button
                              onClick={(e) =>
                                handleDecrease(e, item.variant[0], item)
                              }
                              className="w-7 h-7 flex items-center justify-center text-[#1e4a46] font-bold text-xl hover:bg-white rounded-lg transition-colors"
                            >
                              −
                            </button>
                            <span className="font-bold text-[14px] text-[#1e4a46] min-w-[24px] text-center">
                              {cartQty}
                            </span>
                            <button
                              onClick={(e) =>
                                handleIncrease(e, item.variant[0], item)
                              }
                              className="w-7 h-7 flex items-center justify-center text-[#1e4a46] font-bold text-xl hover:bg-white rounded-lg transition-colors"
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          <button
                            className={`w-full h-9 ${btnCls} disabled:bg-[#c5d5d4] disabled:cursor-not-allowed text-white text-[11px] sm:text-[13px] font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-all duration-200`}
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!localStorage.getItem("authToken")) {
                                setShowLoginPopup(true);
                                return;
                              }
                              await handleAddToCart(item, item.variant[0], 1);
                            }}
                            disabled={isOutOfStock}
                          >
                            <img
                              src={addToCartIcon}
                              alt="Cart"
                              className="w-3.5 h-3.5 object-contain"
                            />
                            {isOutOfStock ? "Out of Stock" : "Add to Cart"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() =>
                  scrollRef.current?.scrollBy({ left: 220, behavior: "smooth" })
                }
                className="absolute -right-3 sm:-right-5 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white border-[1.5px] border-[#e2eaea] shadow-[0_2px_10px_rgba(22,44,42,0.1)] flex items-center justify-center text-[#374747] hover:bg-[#eaf4f3] hover:border-[#5fbdb3] hover:text-[#1e4a46] transition-all duration-200"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {showLoginPopup && <Popup onClose={() => setShowLoginPopup(false)} />}
        </section>
      )}
    </>
  );
};

export default FeaturedProduct_home;
